import {Box, ThemeProvider} from '@primer/react-brand'
import {WebGLPage} from '@github-ui/brand-webgl'
import type {FlexSuiteOverviewPage} from '../types'
import {FlexSuiteHero} from '../components/FlexSuiteHero'
import {FlexSuiteSubNav} from '../components/FlexSuiteSubNav'
import {SupportSection} from './components/SupportSection/SupportSection'
import {FeaturedContent} from './components/FeaturedContent'
import {IntroSection} from './components/IntroSection'
import {OutroSection} from './components/OutroSection'
import {PricingSection} from './components/PricingSection/PricingSection'

export type OverviewTemplateProps = {
  page: FlexSuiteOverviewPage
}

export function OverviewTemplate({page}: OverviewTemplateProps) {
  const {template, settings} = page.fields
  const pageColorMode = settings?.fields.colorMode ?? 'light'
  const {
    settings: templateSettings,
    heroHeading,
    heroLabel,
    heroDescription,
    heroCtaOne,
    heroCtaTwo,
    heroTertiaryDescription,
    heroVariant,
    heroAsset,
    heroCarousel,
    heroIconCards,
    heroImageCards,
    subNav,
    featureHeading,
    featureLabel,
    featureDescription,
    featureLink,
    featureDetailsOne,
    featureRiverAccordion,
    featureRiverStack,
    featureDetailsTwo,
    featureRiverBreakout,
    featureBreakoutTabs,
    introLogoHeading,
    introLogos,
    introLogoLink,
    introContext,
    introContextHeading,
    introContextLink,
    introContextBody,
    introItems,
    introIconCards,
    introPillars,
    introImageCards,
    introStats,
    supportDescription,
    supportLabel,
    supportDetails,
    supportHeading,
    supportIconCards,
    supportPillars,
    supportImageCards,
    supportStats,
    includePricingSection,
    pricingHeading,
    pricingLabel,
    pricingDescription,
    pricingOptions,
    pricingCtas,
    pricingTiles,
    pricingTilesLead,
    featureDetailsThree,
    featureDetailsThreeHeading,
    featureFootstepsLink,
    featureFootstepsLabel,
    featureFootstepsCards,
    featureLargeCards,
    featureDetailsTestimonial,
    featureTestimonialQuote,
    featureTestimonialName,
    featureTestimonialTitle,
    featureTestimonialLink,
    featureTestimonialLabel,
    featureTestimonialLogo,
    featureTestimonialStats,
    featureTestimonialImage,
    outroCustomerStories,
    outroCtaBanner,
    outroCtaHeading,
    outroCtaDescription,
    outroCtaOne,
    outroCtaTwo,
    outroIncludeResources,
    outroResourcesHeading,
    outroResources,
    outroHeading,
    outroFaqs,
  } = template.fields

  const isExpressive = heroVariant === 'Carousel' || heroVariant === 'Stacked'

  return (
    <ThemeProvider colorMode={pageColorMode}>
      {/* The Box component is necessary to set the right background color according to the theme's color mode */}
      <Box backgroundColor="default">
        <WebGLPage mode={pageColorMode}>
          {subNav && <FlexSuiteSubNav subNav={subNav} />}
          <FlexSuiteHero
            heroHeading={heroHeading}
            heroLabel={heroLabel}
            heroDescription={heroDescription}
            heroCtaOne={heroCtaOne}
            heroCtaTwo={heroCtaTwo}
            heroTertiaryDescription={heroTertiaryDescription}
            heroVariant={heroVariant}
            heroAsset={heroAsset}
            heroCarousel={heroCarousel}
            heroIconCards={heroIconCards}
            heroImageCards={heroImageCards}
            brand={templateSettings?.fields.brand}
            showDither
            options={{
              variant: isExpressive ? 'gridline-expressive' : 'gridline',
              align: isExpressive ? 'start' : 'center',
              enableAnimation: true,
              glassBorder: heroVariant === 'Stacked',
            }}
          />
          <IntroSection
            introLogoHeading={introLogoHeading}
            introLogos={introLogos}
            introLogoLink={introLogoLink}
            introContext={introContext}
            introContextHeading={introContextHeading}
            introContextLink={introContextLink}
            introContextBody={introContextBody}
            introItems={introItems}
            introIconCards={introIconCards}
            introPillars={introPillars}
            introImageCards={introImageCards}
            introStats={introStats}
            logobarOptions={{marquee: true, marqueeSpeed: 'slow', variant: 'muted'}}
            logoSuiteOptions={{variant: 'gridline-expressive'}}
          />
          <FeaturedContent
            settings={templateSettings}
            featureHeading={featureHeading}
            featureLabel={featureLabel}
            featureDescription={featureDescription}
            featureLink={featureLink}
            featureDetailsOne={featureDetailsOne}
            featureRiverAccordion={featureRiverAccordion}
            featureRiverStack={featureRiverStack}
            featureDetailsTwo={featureDetailsTwo}
            featureRiverBreakout={featureRiverBreakout}
            featureBreakoutTabs={featureBreakoutTabs}
            featureDetailsThreeHeading={featureDetailsThreeHeading}
            featureDetailsThree={featureDetailsThree}
            featureFootstepsLink={featureFootstepsLink}
            featureFootstepsLabel={featureFootstepsLabel}
            featureFootstepsCards={featureFootstepsCards}
            featureLargeCards={featureLargeCards}
            featureDetailsTestimonial={featureDetailsTestimonial}
            featureTestimonialQuote={featureTestimonialQuote}
            featureTestimonialName={featureTestimonialName}
            featureTestimonialTitle={featureTestimonialTitle}
            featureTestimonialLink={featureTestimonialLink}
            featureTestimonialLabel={featureTestimonialLabel}
            featureTestimonialLogo={featureTestimonialLogo}
            featureTestimonialStats={featureTestimonialStats}
            featureTestimonialImage={featureTestimonialImage}
          />
          <SupportSection
            supportDescription={supportDescription}
            supportHeading={supportHeading}
            supportLabel={supportLabel}
            supportDetails={supportDetails}
            supportIconCards={supportIconCards}
            supportPillars={supportPillars}
            supportImageCards={supportImageCards}
            supportStats={supportStats}
          />
          {includePricingSection && (
            <PricingSection
              pricingHeading={pricingHeading}
              pricingLabel={pricingLabel}
              pricingDescription={pricingDescription}
              pricingOptions={pricingOptions}
              pricingCtas={pricingCtas}
              pricingTiles={pricingTiles}
              pricingTilesLead={pricingTilesLead}
            />
          )}
          <OutroSection
            outroCustomerStories={outroCustomerStories}
            outroCtaBanner={outroCtaBanner}
            outroCtaHeading={outroCtaHeading}
            outroCtaDescription={outroCtaDescription}
            outroCtaOne={outroCtaOne}
            outroCtaTwo={outroCtaTwo}
            outroIncludeResources={outroIncludeResources}
            outroResourcesHeading={outroResourcesHeading}
            outroResources={outroResources}
            outroHeading={outroHeading}
            outroFaqs={outroFaqs}
            settings={templateSettings}
          />
        </WebGLPage>
      </Box>
    </ThemeProvider>
  )
}
